/*
 * DialUpDuo Favorites — first-party wishlist client.
 * Plan: docs/plans/dialupduo-wishlist.md
 *
 * Design contract:
 *  - localStorage is the source of truth. The wishlist works fully for guests
 *    and never depends on login state (New Customer Accounts intermittently
 *    hide the customer from both Liquid and the app proxy, so the server can
 *    only ever ENRICH, not gate).
 *  - Hearts are server-rendered in Liquid ([data-duo-wishlist-toggle]); this
 *    script only hydrates state, so there is zero layout shift.
 *  - Server sync is debounced and full-replace. The worker may answer with a
 *    different (canonical) listId after a login merge; the client adopts it.
 *  - Swishlist migration: on first run we import the retired app's guest list
 *    (st-wishlist-local) and its logged-in mirror (st-wishlist-logged).
 *    Remove this importer around 2026-10 (90 days after cutover).
 */
(() => {
  'use strict';

  if (window.DuoWishlist) return;

  const STORAGE_KEY = 'duo:wishlist:v1';
  const COOKIE_NAME = 'duo_wishlist_id';
  const LEGACY_KEYS = ['st-wishlist-local', 'st-wishlist-logged'];
  const PROXY_ROOT = '/apps/product-options';
  const PAGE_URL = '/pages/wishlist';
  const MAX_ITEMS = 200;
  const SYNC_DEBOUNCE_MS = 1500;
  const SYNC_STALE_MS = 24 * 60 * 60 * 1000;
  const TOAST_MS = 4000;

  const TEXT = {
    saved: 'Saved to Favorites',
    removed: 'Removed from Favorites',
    moved: 'Moved to Favorites',
    undo: 'Undo',
    view: 'View',
    labelAdd: 'Add to Favorites',
    labelSaved: 'Saved to Favorites',
    badge: (n) => (n > 0 ? `Favorites, ${n} ${n === 1 ? 'item' : 'items'}` : 'Favorites')
  };

  /* ------------------------------------------------------------------ *
   *  Storage                                                            *
   * ------------------------------------------------------------------ */

  const canStore = (() => {
    try {
      localStorage.setItem('duo:probe', '1');
      localStorage.removeItem('duo:probe');
      return true;
    } catch (error) {
      return false;
    }
  })();

  let memoryState = null; // fallback when storage is blocked

  function uuid() {
    if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function writeCookie(name, value) {
    try {
      document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (error) {
      /* cookies blocked: localStorage still carries the id */
    }
  }

  function normalizeItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = String(raw.id ?? raw.productId ?? '').trim();
    const handle = String(raw.handle ?? '').trim().toLowerCase();
    if (!/^\d+$/.test(id) || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
    let variantId = raw.variantId == null ? null : String(raw.variantId).trim();
    if (variantId !== null && !/^\d+$/.test(variantId)) variantId = null;
    let addedAt = Number(raw.addedAt);
    if (!Number.isFinite(addedAt) || addedAt <= 0) addedAt = Date.now();
    return { id, handle, variantId, addedAt: Math.floor(addedAt) };
  }

  function migrateFromSwishlist() {
    const found = [];
    for (const key of LEGACY_KEYS) {
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || 'null');
        const entries = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.products) ? parsed.products : [];
        for (const entry of entries) {
          const item = normalizeItem({
            id: entry?.id ?? entry?.productId,
            handle: entry?.handle,
            variantId: entry?.variantId ?? (Array.isArray(entry?.variants) ? entry.variants[0] : null),
            addedAt: entry?.addedDate ? Date.parse(entry.addedDate) : Date.now()
          });
          if (item) found.push(item);
        }
      } catch (error) {
        /* unreadable legacy data: skip */
      }
    }
    return found;
  }

  function dedupe(items) {
    const seen = new Set();
    const out = [];
    for (const item of items) {
      if (!item || seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
      if (out.length >= MAX_ITEMS) break;
    }
    return out;
  }

  function load() {
    if (memoryState) return memoryState;
    let state = null;
    if (canStore) {
      try {
        state = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      } catch (error) {
        state = null;
      }
    }
    if (state && typeof state === 'object' && Array.isArray(state.items) && typeof state.listId === 'string') {
      state.items = dedupe(state.items.map(normalizeItem).filter(Boolean));
      return state;
    }
    const cookieId = readCookie(COOKIE_NAME);
    const migrated = dedupe(migrateFromSwishlist());
    state = {
      listId: /^[0-9a-f-]{36}$/i.test(cookieId) ? cookieId.toLowerCase() : uuid(),
      items: migrated,
      dirty: migrated.length > 0,
      syncedAt: 0,
      // A surviving cookie without local state means storage was cleared or
      // evicted: pull the server copy before ever pushing (a push would wipe it).
      needsRestore: Boolean(cookieId) && migrated.length === 0
    };
    save(state);
    return state;
  }

  function save(state) {
    memoryState = null;
    if (canStore) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        memoryState = state;
      }
    } else {
      memoryState = state;
    }
    writeCookie(COOKIE_NAME, state.listId);
  }

  let state = load();
  let ids = new Set(state.items.map((item) => item.id));

  /* ------------------------------------------------------------------ *
   *  Rendering: hearts, badge, live region, toasts                      *
   * ------------------------------------------------------------------ */

  function paint(button) {
    const saved = ids.has(String(button.dataset.productId));
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    const label = button.querySelector('[data-duo-wishlist-label]');
    if (label) label.textContent = saved ? TEXT.labelSaved : TEXT.labelAdd;
  }

  function hydrate(root) {
    (root || document).querySelectorAll('[data-duo-wishlist-toggle]').forEach(paint);
  }

  function updateBadge() {
    const count = ids.size;
    document.querySelectorAll('[data-duo-wishlist-count]').forEach((bubble) => {
      const number = bubble.querySelector('[data-duo-wishlist-count-number]');
      if (number) number.textContent = count < 100 ? String(count) : '99+';
      bubble.hidden = count === 0;
    });
    document.querySelectorAll('[data-duo-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-label', TEXT.badge(count));
    });
  }

  let liveRegion = null;
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  let toastHost = null;
  function toast(message, action) {
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'duo-wishlist-toasts';
      document.body.appendChild(toastHost);
    }
    const node = document.createElement('div');
    node.className = 'duo-wishlist-toast';
    const text = document.createElement('span');
    text.textContent = message;
    node.appendChild(text);
    if (action && action.href) {
      const link = document.createElement('a');
      link.href = action.href;
      link.textContent = action.label;
      node.appendChild(link);
    } else if (action && action.onClick) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = action.label;
      button.addEventListener('click', () => {
        action.onClick();
        node.remove();
      });
      node.appendChild(button);
    }
    toastHost.appendChild(node);
    while (toastHost.children.length > 3) toastHost.firstElementChild.remove();
    setTimeout(() => {
      node.classList.add('is-leaving');
      setTimeout(() => node.remove(), 300);
    }, TOAST_MS);
  }

  function emitChange() {
    updateBadge();
    document.dispatchEvent(
      new CustomEvent('duo:wishlist:change', { detail: { count: ids.size, items: state.items.slice() } })
    );
    if (typeof publish === 'function') {
      try {
        publish('duo:wishlist:change', { count: ids.size });
      } catch (error) {
        /* pubsub is best-effort */
      }
    }
  }

  /* ------------------------------------------------------------------ *
   *  Mutations                                                          *
   * ------------------------------------------------------------------ */

  function persistAndSync() {
    state.dirty = true;
    save(state);
    emitChange();
    scheduleSync();
  }

  function add(rawItem, options = {}) {
    const item = normalizeItem(rawItem);
    if (!item || ids.has(item.id)) return false;
    if (state.items.length >= MAX_ITEMS) state.items.pop();
    state.items.unshift(item);
    ids.add(item.id);
    persistAndSync();
    hydrateProduct(item.id);
    if (!options.silent) announce(`${TEXT.saved}. ${ids.size} ${ids.size === 1 ? 'item' : 'items'} saved.`);
    return true;
  }

  function remove(productId, options = {}) {
    const id = String(productId);
    if (!ids.has(id)) return false;
    const removed = state.items.find((item) => item.id === id);
    state.items = state.items.filter((item) => item.id !== id);
    ids.delete(id);
    persistAndSync();
    hydrateProduct(id);
    if (!options.silent) {
      announce(`${TEXT.removed}.`);
      toast(TEXT.removed, { label: TEXT.undo, onClick: () => add(removed, { silent: true }) });
    }
    return true;
  }

  function hydrateProduct(productId) {
    document
      .querySelectorAll(`[data-duo-wishlist-toggle][data-product-id="${productId}"]`)
      .forEach(paint);
  }

  function handleToggle(button) {
    const id = String(button.dataset.productId || '');
    if (!id) return;
    if (ids.has(id)) {
      remove(id);
    } else {
      const added = add(
        { id, handle: button.dataset.productHandle, variantId: button.dataset.variantId || null, addedAt: Date.now() },
        { silent: false }
      );
      if (added) {
        button.classList.remove('is-pop');
        void button.offsetWidth; // restart the pop animation
        button.classList.add('is-pop');
        toast(TEXT.saved, { label: TEXT.view, href: PAGE_URL });
      }
    }
  }

  function handleSaveForLater(button) {
    const id = String(button.dataset.productId || '');
    if (!id) return;
    add(
      { id, handle: button.dataset.productHandle, variantId: button.dataset.variantId || null, addedAt: Date.now() },
      { silent: true }
    );
    const row = button.closest('[data-cart-item]');
    const removeButton = row && row.querySelector('cart-remove-button button');
    if (removeButton) removeButton.click();
    announce(`${TEXT.moved}.`);
    toast(TEXT.moved, { label: TEXT.view, href: PAGE_URL });
  }

  /* ------------------------------------------------------------------ *
   *  Server sync (best-effort enrichment; never blocks the UI)          *
   * ------------------------------------------------------------------ */

  let syncTimer = 0;
  let syncing = false;

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => push(false), SYNC_DEBOUNCE_MS);
  }

  function adopt(payload) {
    if (!payload || payload.ok !== true || typeof payload.listId !== 'string') return;
    state.listId = payload.listId.toLowerCase();
    state.items = dedupe((payload.items || []).map(normalizeItem).filter(Boolean));
    state.dirty = false;
    state.needsRestore = false;
    state.syncedAt = Date.now();
    save(state);
    const next = new Set(state.items.map((item) => item.id));
    const changed = next.size !== ids.size || [...next].some((id) => !ids.has(id));
    ids = next;
    if (changed) {
      hydrate(document);
      emitChange();
    }
  }

  async function push(keepalive) {
    if (syncing) return;
    syncing = true;
    const snapshot = JSON.stringify(state.items.map((item) => item.id));
    try {
      const response = await fetch(`${PROXY_ROOT}/wishlist/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        keepalive: Boolean(keepalive),
        body: JSON.stringify({ listId: state.listId, items: state.items })
      });
      if (!response.ok) return;
      const payload = await response.json().catch(() => null);
      // If the user kept clicking while the request was in flight, keep the
      // local truth and try again; otherwise adopt the server's merge result.
      if (JSON.stringify(state.items.map((item) => item.id)) === snapshot) {
        adopt(payload);
      } else {
        scheduleSync();
      }
    } catch (error) {
      /* offline or challenged: local state remains authoritative */
    } finally {
      syncing = false;
    }
  }

  async function pull() {
    try {
      const response = await fetch(`${PROXY_ROOT}/wishlist?list=${encodeURIComponent(state.listId)}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (response.status === 404) {
        state.needsRestore = false;
        state.syncedAt = Date.now();
        save(state);
        return;
      }
      if (!response.ok) return;
      adopt(await response.json().catch(() => null));
    } catch (error) {
      /* best-effort */
    }
  }

  /* ------------------------------------------------------------------ *
   *  Favorites drawer (header heart opens a slide-out, not a nav)        *
   *  Shell: snippets/duo-wishlist-drawer.liquid. Rows are hydrated from  *
   *  /products/{handle}.js; add-to-cart reuses window.DialUpDuoCart.     *
   * ------------------------------------------------------------------ */

  const favDrawer = document.querySelector('[data-duo-fav-drawer]');
  const FAV_RENDER_CAP = 50;
  const favProductCache = new Map();
  let favOpen = false;
  let favLastFocus = null;

  const FAV_HEART_SVG =
    '<svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false">' +
    '<path d="M10 17.5l-1.45-1.32C4.4 12.36 2 10.28 2 7.5 2 5.42 3.42 4 5.5 4c1.17 0 2.3.54 3.04 1.4L10 6.9l1.46-1.5A4 4 0 0 1 14.5 4C16.58 4 18 5.42 18 7.5c0 2.78-2.4 4.86-6.55 8.68L10 17.5z"/></svg>';
  const FAV_ROW_SKELETON =
    '<span class="duo-fav-drawer__img duo-fav-drawer__skel"></span>' +
    '<div class="duo-fav-drawer__info"><span class="duo-fav-drawer__skel duo-fav-drawer__skel--line"></span>' +
    '<span class="duo-fav-drawer__skel duo-fav-drawer__skel--short"></span></div>' +
    '<span></span>';

  function favEscape(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  function favMoney(cents) {
    if (!favDrawer) return '';
    const format = favDrawer.dataset.moneyFormat || '';
    const amount = (Number(cents) || 0) / 100;
    const grouped = (value, precision) => {
      const [whole, fraction] = value.toFixed(precision).split('.');
      const g = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fraction ? `${g}.${fraction}` : g;
    };
    const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (token) {
      const value = grouped(amount, /no_decimals/.test(token[1]) ? 0 : 2);
      return format.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '').trim();
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  function favImg(url, width) {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  async function favFetchOnce(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (response.status === 404) return { gone: true };
    if (!response.ok) throw new Error(String(response.status));
    return response.json(); // throws on a non-JSON body (e.g. a bot challenge)
  }

  function favFetchProduct(handle) {
    if (favProductCache.has(handle)) return favProductCache.get(handle);
    const promise = (async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          return await favFetchOnce(handle);
        } catch (error) {
          if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }
      return null; // transient failure (offline / bot challenge), not a 404
    })();
    favProductCache.set(handle, promise);
    // Never cache a transient failure: let the next open retry.
    promise.then((result) => {
      if (result == null) favProductCache.delete(handle);
    });
    return promise;
  }

  async function favPool(tasks, limit) {
    const queue = tasks.slice();
    await Promise.all(
      Array.from({ length: Math.min(limit, queue.length) }, async () => {
        while (queue.length) await queue.shift()();
      })
    );
  }

  function favRowMarkup(product, item) {
    const image = product.featured_image
      ? `<img class="duo-fav-drawer__img" src="${favEscape(favImg(product.featured_image, 160))}" alt="" width="72" height="72" loading="lazy">`
      : '<span class="duo-fav-drawer__img" aria-hidden="true"></span>';
    const compare = product.compare_at_price && product.compare_at_price > product.price
      ? `<s class="duo-fav-drawer__old">${favMoney(product.compare_at_price)}</s>`
      : '';
    const url = favEscape(product.url || `/products/${item.handle}`);
    let action;
    if (!product.available) {
      action = '<span class="duo-fav-drawer__soldout">Sold out</span>';
    } else if ((product.variants || []).length === 1) {
      action = `<button type="button" class="duo-fav-drawer__add" data-fav-add="${favEscape(product.variants[0].id)}">Add to cart</button>`;
    } else {
      action = `<a class="duo-fav-drawer__options" href="${url}">Choose options</a>`;
    }
    return (
      `<a class="duo-fav-drawer__media" href="${url}" tabindex="-1" aria-hidden="true">${image}</a>` +
      '<div class="duo-fav-drawer__info">' +
      `<a class="duo-fav-drawer__name" href="${url}">${favEscape(product.title)}</a>` +
      `<div class="duo-fav-drawer__price">${compare}<span>${favMoney(product.price)}</span></div>` +
      `<div class="duo-fav-drawer__actions">${action}</div>` +
      '</div>' +
      `<button type="button" class="duo-fav-drawer__remove" data-fav-remove aria-label="Remove ${favEscape(product.title)} from Favorites">${FAV_HEART_SVG}</button>`
    );
  }

  // Product genuinely 404s: it is gone, so say so and offer removal.
  function favGoneMarkup(item) {
    const name = favEscape(item.handle.replace(/-/g, ' '));
    return (
      '<span class="duo-fav-drawer__img" aria-hidden="true"></span>' +
      '<div class="duo-fav-drawer__info">' +
      `<span class="duo-fav-drawer__name">${name}</span>` +
      '<div class="duo-fav-drawer__price"><span>No longer available</span></div>' +
      '</div>' +
      `<button type="button" class="duo-fav-drawer__remove" data-fav-remove aria-label="Remove from Favorites">${FAV_HEART_SVG}</button>`
    );
  }

  // Fetch failed transiently (offline / bot challenge): the product is probably
  // fine, so keep it clickable through to the PDP rather than crying wolf.
  function favErrorMarkup(item) {
    const url = favEscape(`/products/${item.handle}`);
    const name = favEscape(item.handle.replace(/-/g, ' '));
    return (
      `<a class="duo-fav-drawer__media" href="${url}" tabindex="-1" aria-hidden="true"><span class="duo-fav-drawer__img"></span></a>` +
      '<div class="duo-fav-drawer__info">' +
      `<a class="duo-fav-drawer__name" href="${url}">${name}</a>` +
      `<div class="duo-fav-drawer__actions"><a class="duo-fav-drawer__options" href="${url}">View product</a></div>` +
      '</div>' +
      `<button type="button" class="duo-fav-drawer__remove" data-fav-remove aria-label="Remove from Favorites">${FAV_HEART_SVG}</button>`
    );
  }

  function favWireRow(li, item) {
    const removeButton = li.querySelector('[data-fav-remove]');
    if (removeButton) removeButton.addEventListener('click', () => remove(item.id));
    const addButton = li.querySelector('[data-fav-add]');
    if (addButton) addButton.addEventListener('click', () => favAddToCart(addButton.getAttribute('data-fav-add'), addButton));
  }

  async function favHydrateRow(li, item) {
    const product = await favFetchProduct(item.handle);
    if (!li.isConnected) return;
    if (product && product.gone) {
      li.innerHTML = favGoneMarkup(item);
    } else if (!product) {
      li.innerHTML = favErrorMarkup(item);
    } else {
      li.innerHTML = favRowMarkup(product, item);
    }
    favWireRow(li, item);
  }

  function favRender() {
    if (!favDrawer) return;
    const body = favDrawer.querySelector('[data-duo-fav-body]');
    const footer = favDrawer.querySelector('[data-duo-fav-footer]');
    const countEl = favDrawer.querySelector('[data-duo-fav-count]');
    const list = state.items.slice(0, FAV_RENDER_CAP);
    if (countEl) countEl.textContent = state.items.length ? ` (${state.items.length})` : '';
    if (footer) footer.hidden = state.items.length === 0;

    if (!state.items.length) {
      body.innerHTML =
        '<div class="duo-fav-drawer__empty">' +
        `<span class="duo-fav-drawer__empty-icon">${FAV_HEART_SVG.replace('width="18" height="18"', '')}</span>` +
        '<p class="duo-fav-drawer__empty-text">No favorites yet. Tap the heart on any product to save it here.</p>' +
        `<a class="button button--secondary" href="${favEscape(favDrawer.dataset.browseUrl || '/collections/all')}">Browse gifts</a>` +
        '</div>';
      return;
    }

    body.innerHTML = '<ul class="duo-fav-drawer__list" data-fav-list></ul>';
    const listEl = body.querySelector('[data-fav-list]');
    const tasks = list.map((item) => {
      const li = document.createElement('li');
      li.className = 'duo-fav-drawer__item';
      li.dataset.favItem = item.id;
      li.innerHTML = FAV_ROW_SKELETON;
      listEl.appendChild(li);
      return () => favHydrateRow(li, item);
    });
    favPool(tasks, 4);
  }

  async function favAddToCart(variantId, button) {
    const id = Number(variantId);
    if (!id) return;
    button.disabled = true;
    const previous = button.textContent;
    button.textContent = 'Adding';
    try {
      if (window.DialUpDuoCart && typeof window.DialUpDuoCart.addItems === 'function') {
        await window.DialUpDuoCart.addItems([{ id, quantity: 1 }]);
      } else {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] })
        });
        if (!response.ok) throw new Error('add failed');
      }
      closeFav();
    } catch (error) {
      button.disabled = false;
      button.textContent = previous;
      toast('Could not add to cart. Please try again.');
    }
  }

  let favInertedElements = [];

  // Mirror cart-drawer.js _setBackgroundInert: while the Favorites drawer is
  // open the page behind it is inert, so screen-reader virtual cursors and
  // Tab cannot wander into the background.
  function setFavBackgroundInert(makeInert) {
    if (makeInert) {
      favInertedElements = [];
      Array.from(document.body.children).forEach((el) => {
        if (el === favDrawer || el.contains(favDrawer)) return;
        if (/^(SCRIPT|STYLE|LINK)$/.test(el.tagName)) return;
        if (el.hasAttribute('inert')) return;
        el.setAttribute('inert', '');
        favInertedElements.push(el);
      });
    } else {
      favInertedElements.forEach((el) => el.removeAttribute('inert'));
      favInertedElements = [];
    }
  }

  function syncFavTriggerState(isOpen) {
    document.querySelectorAll('[data-duo-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function favTrapFocus(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeFav();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = favDrawer.querySelector('.duo-fav-drawer__panel');
    const focusable = Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openFav() {
    if (!favDrawer || favOpen) return;
    favLastFocus = document.activeElement;
    favRender();
    favDrawer.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => favDrawer.classList.add('is-active'));
    document.documentElement.classList.add('duo-fav-scroll-lock');
    favOpen = true;
    setFavBackgroundInert(true);
    syncFavTriggerState(true);
    document.addEventListener('keydown', favTrapFocus);
    const closeButton = favDrawer.querySelector('[data-duo-fav-close]');
    requestAnimationFrame(() => closeButton && closeButton.focus());
  }

  function closeFav() {
    if (!favDrawer || !favOpen) return;
    favOpen = false;
    setFavBackgroundInert(false);
    syncFavTriggerState(false);
    favDrawer.classList.remove('is-active');
    document.documentElement.classList.remove('duo-fav-scroll-lock');
    document.removeEventListener('keydown', favTrapFocus);
    window.setTimeout(() => {
      if (!favOpen) favDrawer.setAttribute('aria-hidden', 'true');
    }, 400);
    if (favLastFocus && typeof favLastFocus.focus === 'function') {
      try {
        favLastFocus.focus();
      } catch (error) {
        /* element may be gone */
      }
    }
  }

  if (favDrawer) {
    const overlay = favDrawer.querySelector('[data-duo-fav-overlay]');
    const closeButton = favDrawer.querySelector('[data-duo-fav-close]');
    if (overlay) overlay.addEventListener('click', closeFav);
    if (closeButton) closeButton.addEventListener('click', closeFav);
    // The header heart behaves as a dialog trigger only when the drawer
    // exists; without JS it stays a plain link to /pages/wishlist.
    document.querySelectorAll('[data-duo-wishlist-link]').forEach((link) => {
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-expanded', 'false');
    });
    document.addEventListener('duo:wishlist:change', () => {
      if (favOpen) favRender();
    });
  }

  /* ------------------------------------------------------------------ *
   *  Init                                                               *
   * ------------------------------------------------------------------ */

  hydrate(document);
  updateBadge();

  document.addEventListener('click', (event) => {
    // Header heart opens the Favorites drawer instead of navigating. Only plain
    // left-clicks are intercepted; modified clicks (open-in-new-tab) and the
    // no-JS case keep the link's href to /pages/wishlist.
    const favLink = event.target.closest('[data-duo-wishlist-link]');
    if (favLink && favDrawer) {
      if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        openFav();
      }
      return;
    }
    const toggleButton = event.target.closest('[data-duo-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      handleToggle(toggleButton);
      return;
    }
    const saveButton = event.target.closest('[data-duo-wishlist-save-later]');
    if (saveButton) handleSaveForLater(saveButton);
  });

  let observerTimer = 0;
  new MutationObserver(() => {
    clearTimeout(observerTimer);
    observerTimer = setTimeout(() => {
      hydrate(document);
      updateBadge();
    }, 200);
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => hydrate(event.target));

  window.addEventListener('pagehide', () => {
    if (state.dirty) push(true);
  });

  if (state.needsRestore) {
    pull();
  } else if (state.dirty) {
    scheduleSync();
  } else if (state.items.length > 0 && Date.now() - (state.syncedAt || 0) > SYNC_STALE_MS) {
    push(false);
  }

  window.DuoWishlist = {
    pageUrl: PAGE_URL,
    proxyRoot: PROXY_ROOT,
    has: (id) => ids.has(String(id)),
    count: () => ids.size,
    items: () => state.items.slice(),
    listId: () => state.listId,
    add,
    remove,
    toast,
    announce,
    syncNow: () => push(false)
  };
})();
